import { suggestVoiceReading, localFuture } from '../voice-reading.js';
import { dropKitHint } from '../drop-kit-hint.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, prepareImageUpload } from '../api.js';
import { claimRequest, settleRequest } from '../client-ref.js';
import { effectiveParams } from '../params.js';
import { outlierMessage } from '../outlier.js';
import './care.css';
import { confirmPromptFor } from '../reading-confirm.js';
import {
  readSalinityUnit,
  isPlausibleSalinity,
  salinityToPpt,
  formatSalinity,
  salinityUnitLabel,
} from '../salinity.js';
import { getDisplayUnits, cToF, formatTemp } from '../units.js';
import { implausibleReading } from '../../../shared/reading-plausibility.js';
import { doseLogUnitLabel } from '../../../shared/dose-log.js';
import { contactParts, careWhen, careDay } from '../sitter-contact.js';
import { shownFigure, profileVolumeText } from '../shown-units.js';
import { useConfirm } from './ConfirmDialog.jsx';

const when = careWhen;
// Readings are stored in ppt and °F, volumes in gallons; Ask's records and a
// follow-up's before / latest showed those to a keeper on S.G., °C or litres.
const shownUnitsFor = (tank) => ({
  salinityUnit: readSalinityUnit(),
  tempUnit: getDisplayUnits(tank).temp,
  volume: getDisplayUnits(tank).volume,
});
const future = localFuture;
const root = (tank) => `/api/tanks/${tank.id}/care`;

// The tools of the old Today page (Ask, Quick capture, follow-ups) as short
// sheets opened from the Today section on Home (owner, 2026-09-27: one
// place for what matters today). Sitter mode lives in Tank settings.

function CareSheet({ title, eyebrow, onClose, children }) {
  const sheetRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  // Keyboard users land inside the dialog and can leave it with Escape;
  // focus goes back to whatever opened it.
  useEffect(() => {
    const opener = document.activeElement;
    sheetRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (opener && typeof opener.focus === 'function') opener.focus();
    };
  }, []);
  return (
    <div
      className="hv-resident-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div ref={sheetRef} tabIndex={-1} className="hv-resident-sheet care-sheet care-scope">
        <header className="hv-resident-sheet-head">
          <div>
            {eyebrow && <div className="hv-resident-sheet-eyebrow font-heading">{eyebrow}</div>}
            <h3 className="hv-resident-sheet-title">{title}</h3>
          </div>
          <button
            type="button"
            className="hv-resident-sheet-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

export function AskSheet({ tank, onClose, onHistory }) {
  return (
    <CareSheet title="Ask TankSensei" eyebrow={tank.name} onClose={onClose}>
      <AskTank
        key={tank.id}
        tank={tank}
        onHistory={(param) => {
          onClose();
          onHistory?.(param);
        }}
      />
    </CareSheet>
  );
}

export function CaptureSheet({ tank, onClose, onSaved, onLog }) {
  return (
    <CareSheet title="Say it" eyebrow={tank.name} onClose={onClose}>
      <QuickCapture tank={tank} onSaved={async () => onSaved?.()} onLog={onLog} />
    </CareSheet>
  );
}

export const openFollowUps = (plans) =>
  (Array.isArray(plans) ? plans : []).filter((p) => p?.status === 'active');

// A "Follow up:" task belongs to the plan that created it. Matching on the
// plan's task_id, never on the title: a keeper's own task can be called
// "Follow up: call the store" too.
export function followUpPlanIdByTask(plans) {
  const out = new Map();
  for (const p of openFollowUps(plans)) if (p.task_id) out.set(p.task_id, p.id);
  return out;
}

// A follow-up is a care plan: something to check again later, with the
// reading it started from. `start` picks the first screen: 'list' (every
// open follow-up), 'new' (optionally prefilled with a title and param), or
// the plan named by `planId`.
export function FollowUpSheet({ tank, start, planId, prefill, onClose, onSaved, onHistory }) {
  const editable = tank.role !== 'viewer';
  const [data, setData] = useState(null),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false),
    [screen, setScreen] = useState(() =>
      planId ? { kind: 'plan', id: planId } : { kind: start === 'new' ? 'new' : 'list' }
    ),
    [draft, setDraft] = useState({
      title: prefill?.title || '',
      param: prefill?.param || null,
      due: future(1),
    });
  const reload = useCallback(async () => {
    const next = await api(root(tank));
    setData(next);
    // A plan has to start from a logged reading; a prefilled param with
    // none would only be refused on save.
    setDraft((d) =>
      d.param && !next.readings.some((r) => r.param === d.param) ? { ...d, param: null } : d
    );
  }, [tank.id]);
  useEffect(() => {
    reload().catch((e) => setError(e.message));
  }, [reload]);
  async function act(fn) {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (e) {
      setError(e.message);
      setBusy(false);
      return false;
    }
    // Saved. A refresh that fails on a weak signal must not read as a failed
    // save: the keeper would tap again and make a second follow-up.
    try {
      await reload();
      await onSaved?.();
    } catch {
      /* the next open shows the saved state */
    }
    setBusy(false);
    return true;
  }
  const open = openFollowUps(data?.plans);
  const plan = screen.kind === 'plan' ? data?.plans.find((p) => p.id === screen.id) : null;
  const title =
    screen.kind === 'new' ? 'New follow-up' : screen.kind === 'plan' ? 'Follow-up' : 'Follow-ups';
  return (
    <CareSheet title={title} eyebrow={tank.name} onClose={onClose}>
      {error && (
        <p role="alert" className="care-error">
          {error}
        </p>
      )}
      {!data ? (
        <p role="status">Loading…</p>
      ) : screen.kind === 'list' ? (
        <div className="care-card">
          {open.length === 0 ? (
            <p>No follow-ups are open for this tank.</p>
          ) : (
            open.map((p) => (
              <div className="care-row" key={p.id}>
                <span>
                  {p.title}
                  <small>
                    Follow-up {when(p.due)}
                    {p.action_at ? ' · action recorded' : ''}
                  </small>
                </span>
                <button className="btn" onClick={() => setScreen({ kind: 'plan', id: p.id })}>
                  Open
                </button>
              </div>
            ))
          )}
          {editable && (
            <div className="care-actions care-list-actions">
              <button className="btn btn-primary" onClick={() => setScreen({ kind: 'new' })}>
                New follow-up
              </button>
            </div>
          )}
        </div>
      ) : screen.kind === 'plan' ? (
        <>
          {plan ? (
            <PlanCard
              plan={plan}
              reading={data.readings.find((r) => r.param === plan.param)}
              units={shownUnitsFor(tank)}
              editable={editable}
              busy={busy}
              onSave={(body) =>
                act(() => api(`${root(tank)}/plans/${plan.id}`, { method: 'PATCH', body }))
              }
              onHistory={onHistory}
            />
          ) : (
            <p>This follow-up is no longer open. It may have been resolved already.</p>
          )}
          <button className="btn" onClick={() => setScreen({ kind: 'list' })}>
            All open follow-ups ({open.length})
          </button>
        </>
      ) : (
        <form
          className="care-form"
          onSubmit={async (e) => {
            e.preventDefault();
            const ok = await act(() =>
              api(`${root(tank)}/plans`, {
                method: 'POST',
                body: { ...draft, due: new Date(draft.due).toISOString() },
              })
            );
            if (ok) onClose();
          }}
        >
          <p>
            Something to check again later. The reading it starts from is kept, so you can compare
            when the time comes, and it appears in Today when it is due.
          </p>
          <label>
            What needs attention?
            <input
              required
              maxLength={100}
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            />
          </label>
          <label>
            Compare a parameter
            <select
              value={draft.param || ''}
              onChange={(e) => setDraft({ ...draft, param: e.target.value || null })}
            >
              <option value="">Observation only</option>
              {data.readings.map((r) => (
                <option key={r.param} value={r.param}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Follow-up time
            <input
              required
              type="datetime-local"
              value={draft.due}
              onChange={(e) => setDraft({ ...draft, due: e.target.value })}
            />
          </label>
          <div className="care-actions">
            <button className="btn btn-primary" disabled={busy}>
              Save follow-up
            </button>
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </CareSheet>
  );
}

function PlanCard({ plan: p, reading, units, editable, busy, onSave, onHistory }) {
  const [note, setNote] = useState('');
  return (
    <article className="care-plan">
      <small>
        {p.status} · Follow-up {when(p.due)}
      </small>
      <h3>{p.title}</h3>
      <p>{p.follow_up.message}</p>
      {p.baseline && (
        <p>
          Before: {shownFigure(p.param, p.baseline.value, p.baseline.unit, units)} (
          {when(p.baseline.ts)})
          {reading && (
            <>
              {' '}
              · Latest: {shownFigure(p.param, reading.value, reading.unit, units)} (
              {when(reading.ts)})
            </>
          )}
        </p>
      )}
      {p.action_note && (
        <p>
          Your action: {p.action_note} · {when(p.action_at)}
        </p>
      )}
      {p.action_at && <small>A change after an action does not prove the action caused it.</small>}
      {p.param && (
        <button className="btn" onClick={() => onHistory(p.param)}>
          View parameter history
        </button>
      )}
      {p.status === 'active' && editable && (
        <div className="care-form">
          <label>
            What did you do or observe?
            <textarea maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <div className="care-actions">
            <button
              className="btn"
              disabled={busy || !note.trim()}
              onClick={async () => {
                if (await onSave({ action_note: note })) setNote('');
              }}
            >
              Record action
            </button>
            <button
              className="btn"
              disabled={busy || !note.trim()}
              onClick={() => onSave({ status: 'resolved', outcome: note })}
            >
              Resolve with outcome
            </button>
            <button
              className="btn"
              disabled={busy || !note.trim()}
              onClick={() => onSave({ status: 'dismissed', outcome: note })}
            >
              Close without action
            </button>
          </div>
        </div>
      )}
    </article>
  );
}

function AskTank({ tank, onHistory }) {
  const units = shownUnitsFor(tank);
  const [question, setQuestion] = useState(''),
    [messages, setMessages] = useState([]),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  async function ask(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await api(`${root(tank)}/ask`, {
        method: 'POST',
        body: { question, salinity_unit: readSalinityUnit() },
      });
      setMessages([...messages, { question, ...result }]);
      setQuestion('');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="care-card">
      <h2>Ask about {tank.name}</h2>
      <p>
        Answers use available tank records and show their sources. Each question is answered
        independently.
      </p>
      {messages.map((m, i) => (
        <article key={i} className="care-plan">
          <h3>{m.question}</h3>
          {m.paragraphs.map((p, j) => (
            <div key={j}>
              <p>{p.text}</p>
              <details>
                <summary>Supporting records</summary>
                {p.evidence_ids.map((id) => {
                  const ev = m.evidence.find((e) => e.id === id);
                  return (
                    <div key={id} className="care-row">
                      <span>
                        {ev.label || ev.param || ev.kind}{' '}
                        {shownFigure(ev.param, ev.value ?? ev.median ?? '', ev.unit, units)}
                        {ev.kind === 'recorded_dose' && (
                          <>
                            {' · '}
                            {ev.unit && ev.amount != null
                              ? `${Number(ev.amount)} ${doseLogUnitLabel(ev.unit)}`
                              : `${ev.amount_ml} mL`}{' '}
                            recorded
                          </>
                        )}
                        {ev.kind === 'daily_median' && <> · daily median ({careDay(ev.day)})</>}
                        <small>
                          {ev.ts
                            ? when(ev.ts)
                            : ev.sampled_at
                              ? when(ev.sampled_at)
                              : 'Tank profile / report'}
                          {ev.text && ` · ${ev.text}`}
                          {ev.kind === 'profile' &&
                            ` · ${ev.name}; ${profileVolumeText(ev.volume_gallons, units.volume)}`}
                          {ev.kind === 'reading' && !ev.trustworthy && ' · Confirmation needed'}
                        </small>
                      </span>
                      {ev.kind === 'lab_report' && (
                        <pre className="care-evidence">{JSON.stringify(ev.panel, null, 2)}</pre>
                      )}
                      {ev.kind === 'profile' && (
                        <pre className="care-evidence">
                          {JSON.stringify(
                            { livestock: ev.livestock, equipment: ev.equipment },
                            null,
                            2
                          )}
                        </pre>
                      )}
                      {ev.param && (
                        <button className="btn" onClick={() => onHistory(ev.param)}>
                          History
                        </button>
                      )}
                    </div>
                  );
                })}
              </details>
            </div>
          ))}
        </article>
      ))}
      {error && (
        <p role="alert" className="care-error">
          {error}
        </p>
      )}
      <form className="care-form" onSubmit={ask}>
        <label>
          Your question
          <textarea
            required
            maxLength={1000}
            value={question}
            placeholder="What do my latest readings tell us?"
            onChange={(e) => setQuestion(e.target.value)}
          />
        </label>
        <button className="btn btn-primary" disabled={busy || tank.role === 'viewer'}>
          {busy ? 'Reading your records…' : 'Ask TankSensei'}
        </button>
      </form>
    </section>
  );
}

function QuickCapture({ tank, onSaved, onLog }) {
  const [transcript, setTranscript] = useState(''),
    [mode, setMode] = useState('note'),
    [param, setParam] = useState(() => effectiveParams(tank)[0]?.key || 'alk'),
    [value, setValue] = useState('');
  const [listening, setListening] = useState(false),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false),
    [review, setReview] = useState(null),
    [saved, setSaved] = useState(false);
  const recognition = useRef(null);
  useEffect(() => () => recognition.current?.abort(), []);
  const params = effectiveParams(tank);
  const config = params.find((p) => p.key === param);
  // What the keeper types in: their salinity scale, °C on a Celsius tank.
  // Storage stays ppt and °F.
  const tempUnit = getDisplayUnits(tank).temp;
  const inputUnit =
    param === 'salinity'
      ? salinityUnitLabel(readSalinityUnit())
      : param === 'temp'
        ? `°${tempUnit}`
        : config?.unit || '';
  const Speech = window.SpeechRecognition || window.webkitSpeechRecognition;
  function listen() {
    setError('');
    setReview(null);
    setSaved(false);
    const r = new Speech();
    recognition.current = r;
    r.lang = navigator.language || 'en-US';
    r.interimResults = false;
    r.onresult = (e) => {
      const spoken = e.results[0][0].transcript;
      setTranscript(spoken);
      setReview(null);
      if (mode === 'reading') {
        const suggestion = suggestVoiceReading(spoken, params, param);
        if (suggestion) {
          setParam(suggestion.param);
          setValue(suggestion.value);
        } else {
          setValue('');
          setError('Check the dictated result and enter one value in the displayed units.');
        }
      }
    };
    r.onerror = (e) => {
      setError(`Dictation unavailable (${e.error}). You can type below.`);
      setListening(false);
    };
    r.onend = () => setListening(false);
    setListening(true);
    try {
      r.start();
    } catch {
      setListening(false);
      setError('Dictation could not start. You can type your note or reading.');
    }
  }
  async function preview(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    setSaved(false);
    try {
      if (mode === 'reading') {
        // The iOS decimal pad types "8,4" in comma locales.
        const typed = value.trim().replace(',', '.');
        if (!typed || !Number.isFinite(Number(typed)))
          throw new Error('Type the reading as a number, like 8.4.');
        // Salinity is stored in ppt; a spoken or typed 1.026 is S.G. and
        // was saved as 1.026 ppt before this converted it.
        const salUnit = readSalinityUnit();
        if (param === 'salinity' && !isPlausibleSalinity(Number(typed), salUnit)) {
          throw new Error('That does not look like a salinity reading. Check the number.');
        }
        const raw = Number(typed);
        const saveValue =
          param === 'salinity'
            ? salinityToPpt(raw, salUnit)
            : param === 'temp' && tempUnit === 'C'
              ? Math.round(cToF(raw) * 10) / 10
              : raw;
        const wrong = implausibleReading(param, saveValue, {
          subtypeSlug: tank.subtype_slug,
          shown:
            param === 'temp' || param === 'salinity'
              ? `${raw}${param === 'temp' ? `°${tempUnit}` : ''}`
              : null,
        });
        if (wrong) throw new Error(wrong.message);
        const check = await api(`/api/tanks/${tank.id}/readings/check`, {
          method: 'POST',
          body: { param, value: saveValue },
        });
        if (check?.implausible) throw new Error(check.implausible.message);
        setReview({
          param,
          value: saveValue,
          unit: config?.unit || '',
          method: 'manual',
          note: transcript.trim() ? `Voice/text capture: ${transcript}`.slice(0, 1000) : null,
          shown:
            param === 'salinity'
              ? `${formatSalinity(saveValue, salUnit)} ${salinityUnitLabel(salUnit)}`
              : param === 'temp'
                ? formatTemp(saveValue, tempUnit)
                : `${saveValue} ${config?.unit || ''}`.trim(),
          warning:
            confirmPromptFor(tank, param, saveValue, { salinityUnit: salUnit }) ||
            (check?.outlier_reason
              ? `Unusual reading: ${outlierMessage(check, tank)} Retest if uncertain.`
              : null),
        });
      } else {
        if (!transcript.trim()) throw new Error('Add a note first.');
        setReview({ body: transcript.trim() });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  async function save() {
    setBusy(true);
    setError('');
    try {
      const { shown: _shown, warning: _warning, ...body } = review;
      const scope = `journal:${tank.id}`;
      await api(`/api/tanks/${tank.id}/${mode === 'note' ? 'journal/notes' : 'readings'}`, {
        method: 'POST',
        body:
          mode === 'note'
            ? { ...body, client_ref: claimRequest(scope, JSON.stringify([body.body, 0])) }
            : body,
      });
      if (mode === 'note') settleRequest(scope);
      setReview(null);
      setTranscript('');
      setValue('');
      setSaved(true);
      await onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="care-card">
      <h2>Say it or type it</h2>
      <p>
        Dictate an observation or test result. Review the text, value, and units before saving. Your
        browser may use its speech service.
      </p>
      {Speech ? (
        <button
          className="btn"
          disabled={busy || tank.role === 'viewer'}
          onClick={() => (listening ? recognition.current?.stop() : listen())}
        >
          {listening ? 'Stop listening' : 'Dictate'}
        </button>
      ) : (
        <p>Browser dictation is unavailable here. Use your keyboard’s microphone or type below.</p>
      )}
      <button className="btn" onClick={onLog}>
        Photo test / ICP capture
      </button>
      {error && (
        <p className="care-error" role="alert">
          {error}
        </p>
      )}
      {saved && <p role="status">Saved to your tank.</p>}
      <form className="care-form" onSubmit={preview}>
        <label>
          Save as
          <select
            value={mode}
            onChange={(e) => {
              setMode(e.target.value);
              setReview(null);
            }}
          >
            <option value="note">Journal observation</option>
            <option value="reading">One test reading</option>
          </select>
        </label>
        <label>
          Dictated text / note
          <textarea
            maxLength={900}
            value={transcript}
            onChange={(e) => {
              setTranscript(e.target.value);
              setReview(null);
            }}
          />
        </label>
        {mode === 'reading' && (
          <>
            <label>
              Parameter
              <select
                value={param}
                onChange={(e) => {
                  setParam(e.target.value);
                  setReview(null);
                }}
              >
                {params.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label || p.key}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Confirmed value ({inputUnit || 'unitless'})
              <input
                required
                inputMode="decimal"
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  setReview(null);
                }}
              />
            </label>
            <small>
              Enter the value from your dictated result in the displayed units. Nothing is inferred
              from ambiguous speech.
            </small>
            {dropKitHint(param) && <small>{dropKitHint(param)}</small>}
          </>
        )}
        <button className="btn" disabled={busy || listening || tank.role === 'viewer'}>
          Review before saving
        </button>
      </form>
      {review && (
        <div className="care-plan">
          <h3>Confirm for {tank.name}</h3>
          <p>{mode === 'note' ? review.body : `${config?.label || param}: ${review.shown}`}</p>
          {review.warning && <p role="alert">{review.warning}</p>}
          <button className="btn btn-primary" disabled={busy || listening} onClick={save}>
            {review.warning ? 'Yes, save it' : 'Confirm & save'}
          </button>
        </div>
      )}
    </section>
  );
}

export function SitterSetup({ tank }) {
  const confirm = useConfirm();
  const [data, setData] = useState(null),
    [link, setLink] = useState(''),
    [linkId, setLinkId] = useState(null),
    [copied, setCopied] = useState(false),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: '',
    instructions: '',
    contact: '',
    checklist: 'Check the tank and equipment\nFeed only the prepared portion',
    expires_at: future(7),
  });
  const reload = useCallback(async () => setData(await api(`${root(tank)}/sitters`)), [tank.id]);
  useEffect(() => {
    reload().catch((e) => setError(e.message));
  }, [reload]);
  async function create(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const r = await api(`${root(tank)}/sitters`, {
        method: 'POST',
        body: {
          ...form,
          checklist: form.checklist.split('\n'),
          expires_at: new Date(form.expires_at).toISOString(),
        },
      });
      setLink(`${window.location.origin}${r.path}`);
      setLinkId(r.id);
      await reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  async function revoke(id, name) {
    // One stray tap ended a sitter's link while the owner was away.
    const ok = await confirm({
      title: `Revoke ${name || 'this sitter'}'s link?`,
      body: 'The link stops working at once. You can create a new one, but it has to be sent again.',
      confirmLabel: 'Revoke access',
      cancelLabel: 'Keep it',
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api(`${root(tank)}/sitters/${id}`, { method: 'DELETE' });
      if (linkId === id) setLink('');
      await reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="care-card">
      <h2>Care while you’re away</h2>
      <p>
        Create an expiring link with your instructions and check-in tools. Anyone holding the link
        can use it until you revoke it or it expires. It does not allow dosing, equipment control,
        or account access.
      </p>
      {error && (
        <p role="alert" className="care-error">
          {error}
        </p>
      )}
      <form className="care-form" onSubmit={create}>
        {[
          ['name', 'Sitter name', 80],
          ['contact', 'Emergency contact and when to call', 300],
          ['instructions', 'Feeding amounts, equipment checks, and what to avoid', 3000],
          ['checklist', 'Checklist — one item per line', 4000],
        ].map(([k, label, max]) => (
          <label key={k}>
            {label}
            <textarea
              required
              maxLength={max}
              value={form[k]}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })}
            />
          </label>
        ))}
        <label>
          Access expires
          <input
            required
            type="datetime-local"
            value={form.expires_at}
            onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
          />
        </label>
        <button className="btn btn-primary" disabled={busy}>
          Create sitter link
        </button>
      </form>
      {link && (
        <div className="care-form">
          <label>
            Copy and send this link yourself
            <input readOnly value={link} onFocus={(e) => e.target.select()} />
          </label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn"
              style={{ minHeight: 44 }}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(link);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                } catch {
                  /* the field above is selectable as a fallback */
                }
              }}
            >
              {copied ? 'Copied' : 'Copy link'}
            </button>
            {typeof navigator !== 'undefined' && navigator.share && (
              <button
                type="button"
                className="btn"
                style={{ minHeight: 44 }}
                onClick={() =>
                  navigator
                    .share({ title: `Tank sitter: ${tank.name || 'my tank'}`, url: link })
                    .catch(() => {})
                }
              >
                Share
              </button>
            )}
          </div>
          <small>The secret link is shown only now. Create a replacement if you lose it.</small>
        </div>
      )}
      {data?.passes.map((p) => (
        <div className="care-row" key={p.id}>
          <span>
            {p.name}
            <small>{p.revoked_at ? 'Revoked' : `Expires ${when(p.expires_at)}`}</small>
          </span>
          {!p.revoked_at && (
            <button className="btn" disabled={busy} onClick={() => revoke(p.id, p.name)}>
              Revoke access
            </button>
          )}
        </div>
      ))}
      <h3>Recent check-ins</h3>
      {data?.checkins.map((c) => (
        <article className="care-plan" key={c.id}>
          <strong>
            {c.name} · {when(c.created_at)}
          </strong>
          <p>{c.note}</p>
          <p>
            Completed:{' '}
            {c.completed
              .map(
                (i) => data.passes.find((p) => p.id === c.pass_id)?.checklist[i] || 'Checklist item'
              )
              .join(', ') ||
              (c.note
                ? 'Observation only'
                : c.has_photo
                  ? 'Photo only'
                  : 'Visit noted, no note or photo')}
          </p>
          {c.has_photo && (
            <img
              className="care-photo"
              alt="Sitter check-in"
              src={`${root(tank)}/checkins/${c.id}/photo`}
            />
          )}
        </article>
      ))}
    </section>
  );
}

export function SitterPage() {
  const token = useRef(window.location.hash.slice(1));
  const [data, setData] = useState(null),
    [note, setNote] = useState(''),
    [completed, setCompleted] = useState([]),
    [file, setFile] = useState(null),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false),
    [saved, setSaved] = useState(false),
    [loading, setLoading] = useState(true),
    // The visit a photo did not reach: the next Save sends it there instead
    // of starting a second, empty visit.
    [retryId, setRetryId] = useState(null);
  const photoInputRef = useRef(null);
  async function request(path = '', options = {}) {
    const r = await fetch(`/api/sitter${path}`, {
      ...options,
      credentials: 'omit',
      headers: { Authorization: `Bearer ${token.current}`, ...options.headers },
    });
    const body = await r.json();
    if (!r.ok)
      throw Object.assign(new Error(body.error || 'Check-in failed'), { status: r.status });
    return body;
  }
  useEffect(() => {
    request()
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);
  async function sendPhoto(id) {
    try {
      const photo = await prepareImageUpload(file, { maxDim: 1200, quality: 0.75 });
      await request(`/checkins/${id}/photo`, {
        method: 'POST',
        headers: { 'Content-Type': photo.mime },
        body: photo.body,
      });
      setFile(null);
      setRetryId(null);
      // The field kept showing the sent photo, so the next visit looked
      // as if it had one attached.
      if (photoInputRef.current) photoInputRef.current.value = '';
    } catch (e) {
      setRetryId(id);
      setError(
        e.status === 422
          ? 'Your visit was noted, but that photo could not be read. Choose a JPEG or PNG and tap Save check-in to send it.'
          : 'Your visit was noted, but the photo did not arrive. Tap Save check-in to send it again.'
      );
    }
  }
  async function save(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    setSaved(false);
    try {
      if (retryId && file && !note.trim() && !completed.length) {
        await sendPhoto(retryId);
        setSaved(true);
        setData(await request());
        return;
      }
      const r = await request('/checkins', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note, completed, with_photo: Boolean(file) }),
      });
      setRetryId(null);
      setSaved(true);
      setNote('');
      setCompleted([]);
      if (file) await sendPhoto(r.id);
      setData(await request());
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="care-page care-sitter">
      <h1>Tank sitter</h1>
      {error && (
        <p role="alert" className="care-error">
          {error}
        </p>
      )}
      {loading ? (
        <p role="status">Opening the sitter page…</p>
      ) : !data ? (
        <p>Open the current link from the tank owner.</p>
      ) : (
        <>
          <h2>{data.tank_name}</h2>
          <p>
            For {data.name} · Access until {when(data.expires_at)}
          </p>
          <section className="care-card">
            <h3>Owner’s instructions</h3>
            <p className="care-preserve">{data.instructions}</p>
            <h3>If something is wrong</h3>
            <p className="care-preserve">
              {contactParts(data.contact).map((part, i) =>
                part.tel ? (
                  <a
                    key={i}
                    href={`tel:${part.tel}`}
                    style={{ display: 'inline-block', minHeight: 44, lineHeight: '44px' }}
                  >
                    {part.text}
                  </a>
                ) : (
                  <span key={i}>{part.text}</span>
                )
              )}
            </p>
          </section>
          <form className="care-card care-form" onSubmit={save}>
            <h3>This visit</h3>
            {data.checklist.map((item, i) => (
              <label className="care-check" key={i}>
                <input
                  type="checkbox"
                  checked={completed.includes(i)}
                  onChange={(e) =>
                    setCompleted(
                      e.target.checked ? [...completed, i] : completed.filter((n) => n !== i)
                    )
                  }
                />
                {item}
              </label>
            ))}
            <label>
              What did you notice?
              <textarea maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
            </label>
            <label>
              Optional tank photo
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </label>
            <button className="btn btn-primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save check-in'}
            </button>
            {saved && <p role="status">Check-in saved. The owner can see it in sitter mode.</p>}
          </form>
          <section className="care-card">
            <h3>Recent visits</h3>
            {data.checkins.map((c) => (
              <div className="care-row" key={c.id}>
                <span>
                  {when(c.created_at)}
                  <p>{c.note}</p>
                  <small>{c.completed.map((i) => data.checklist[i]).join(', ')}</small>
                </span>
              </div>
            ))}
          </section>
        </>
      )}
    </main>
  );
}
